import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Contract,
  ContractReplacement,
  DocumentRecord,
  Milestone,
  MilestoneMessage,
} from '../../types';
import { contractApi, milestoneApi } from '../../services/api';
import { combinedAgreementTimeline } from '../../utils/combinedAgreementTimeline';
import { stageVariationOre } from '../../utils/stageVariation';
import { useCompanyTheme } from '../company-theme/useCompanyTheme';
import Modal from '../Modal';
import ContractReferenceNote from '../ContractReferenceNote';
import ConflictVariationNotice from '../ConflictVariationNotice';
import { ContractWizardModal } from './ContractWizardModal';
import { AssignmentStep } from './AssignmentStep';
import { AttachmentLibraryPicker } from './AttachmentLibraryPicker';
import { useContractWizardDraft } from './useContractWizardDraft';
import {
  useVariationPlan,
  variationProjectAdjustment,
} from './useVariationPlan';
import {
  VariationPlanStep,
  VariationTotals,
  variationMoney,
} from './VariationPlanStep';
import { VariationChanges } from './VariationChanges';
import { ReviewStep } from './ReviewStep';
import { useReviewStep } from './useReviewStep';
import { ContractDocumentPreview } from './ContractDocumentPreview';
import { previewFromContract } from './contractPreviewData';
import { useDraftMessages } from './DraftMessageHistory';
import { agreementLabel, agreementVariationNumber } from '../../utils/agreementIdentity';
import { apiErrorCode, apiErrorText } from '../../utils/apiError';

export const variationReturnKey = (projectId: string) =>
  `etappio:variation-return:${projectId}`;
interface Props {
  contract: Contract;
  stages: Milestone[];
  contracts: Contract[];
  allStages: Milestone[];
  entryStageId?: string;
  initialReason: string;
  reference?: MilestoneMessage;
  existing?: Contract;
  settlement?: ContractReplacement;
  initialStep?: number;
  /**
   * "Förhandsgranska": open the ÄTA as a read-only document first. With
   * `canSign`, "Gå till signering" then switches to the review step.
   */
  viewOnly?: boolean;
  canSign?: boolean;
  company: { name: string; organizationNumber?: string };
  customer: { name?: string; email?: string };
  providerName: string;
  onBeforeChange: (id: string) => Promise<boolean>;
  onPersisted: (
    contract: Contract,
    settlement?: ContractReplacement,
  ) => Promise<void>;
  onOpenAttachment: (doc: DocumentRecord) => void;
  onVerifyAttachment: (doc: DocumentRecord) => void;
  onClose: (created?: Contract) => void;
}
export function VariationWizard(props: Props) {
  const { t } = useTranslation();
  const parent = props.contract;
  const [settlement, setSettlement] = useState<ContractReplacement | null>(
    props.settlement ?? null,
  );
  const [loading, setLoading] = useState(true);
  const [withdrawn, setWithdrawn] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const signingLocked = !withdrawn && !!props.existing && props.existing.draftStatus !== 'draft_rejected' &&
    (props.existing.companySigned || props.existing.personSigned || ['sent', 'pending_signature', 'signed'].includes(props.existing.signingStatus ?? ''));
  const modeLocked = !!(props.existing?.signingSentAt || props.existing?.externalSigningProcessId);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(!!props.viewOnly);
  const messages = useDraftMessages(props.existing?.id);
  const [priceOnly, setPriceOnly] = useState(
    props.existing?.settlementType === 'price_adjustment',
  );
  const [priceAmount, setPriceAmount] = useState(
    props.existing?.settlementType === 'price_adjustment'
      ? String(props.existing.afterRotAmount)
      : '0',
  );
  const [priceSaving, setPriceSaving] = useState(false);
  const [savedPrice, setSavedPrice] = useState(priceAmount);
  const [switching, setSwitching] = useState(false);
  const [inheritedTerms, setInheritedTerms] = useState('');
  const [showBasis, setShowBasis] = useState(false);
  const [basisDescriptions, setBasisDescriptions] = useState({
    contractName: '',
    assignmentDescription: '',
    otherDescription: '',
    referToAttachments: false,
  });
  const [revision, setRevision] = useState(0);
  const latest = useRef(props);
  latest.current = props;
  const discarded = useRef(false);
  const draft = useContractWizardDraft(parent.projectId, {
    variation: true,
    createDraft: async (description) => {
      if (
        props.entryStageId &&
        !(await props.onBeforeChange(props.entryStageId))
      )
        return null;
      const result = await milestoneApi.createContractStageVariation(
        parent.id,
        {
          reason: description,
          selectedMilestoneIds: [],
          entryMilestoneId: props.entryStageId,
          referenceMessageId: props.reference?.id,
        },
      );
      if (!result.contract) throw Error('Missing amendment');
      setSettlement(result);
      await latest.current.onPersisted(result.contract, result);
      return result.contract;
    },
  });
  const plan = useVariationPlan(
    settlement,
    props.stages,
    async (result) => {
      setSettlement(result);
      if (result.contract) {
        draft.replaceContract(result.contract);
        await latest.current.onPersisted(result.contract, result);
      }
      setRevision((value) => value + 1);
    },
    props.onBeforeChange,
    signingLocked,
  );
  const review = useReviewStep(draft.contract?.id);
  const theme = useCompanyTheme(
    draft.contract ? { contractId: draft.contract.id } : { own: true },
  );
  const timeline = combinedAgreementTimeline(
    parent,
    props.contracts,
    props.allStages,
  );
  const root = timeline?.root ?? parent;
  const basisVariation = agreementVariationNumber(parent);
  const entryStage = props.stages.find(stage => stage.id === props.entryStageId);
  const projectAdjustmentOre = variationProjectAdjustment(
    parent,
    props.contracts,
  );
  const totalsParent = {
    ...parent,
    afterRotAmount: String(
      Number(parent.afterRotAmount) + projectAdjustmentOre / 100,
    ),
  };
  const origins = Object.fromEntries(
    props.stages.map((stage) => {
      const context = timeline?.contexts[stage.id];
      return [
        stage.id,
        context && context.contract.id !== root.id
          ? t('variationWizard.originVariation', {
              number: (context.contract.contractVersion ?? 2) - 1,
            })
          : t('variationWizard.base'),
      ];
    }),
  );
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const descriptions = await contractApi.getContractDescriptions(parent.id);
      if (cancelled) return;
      const terms = [
        descriptions.other?.content,
        descriptions.variationOther?.content,
      ]
        .filter(Boolean)
        .join('\n');
      setInheritedTerms(terms);
      setBasisDescriptions({
        contractName: descriptions.contractName?.content ?? '',
        assignmentDescription:
          descriptions.assignmentDescription?.content ?? '',
        otherDescription: terms,
        referToAttachments: descriptions.referToAttachments?.content === 'true',
      });
      if (props.existing) {
        if (signingLocked) draft.setWriteGuard(async () => false);
        await draft.load(props.existing);
        const existingDescriptions = await contractApi.getContractDescriptions(
          props.existing.id,
        );
        if (!cancelled)
          setInheritedTerms(existingDescriptions.other?.content ?? terms);
      } else {
        draft.setField('assignmentDescription', props.initialReason);
        if (props.entryStageId) draft.setField('variationReason', 'dispute');
      }
      if (!cancelled) setLoading(false);
    })().catch(() => {
      if (!cancelled) {
        setError(t('variationWizard.loadError'));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // Load once per wizard; autosaves must not replace fields under the user's cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const priceRef = useRef({ priceAmount, priceOnly, contract: draft.contract });
  priceRef.current = { priceAmount, priceOnly, contract: draft.contract };
  const priceRequest = useRef<Promise<boolean> | null>(null);
  const savePrice = async (): Promise<boolean> => {
    if (signingLocked) return false;
    while (priceRequest.current) {
      if (!(await priceRequest.current)) return false;
    }
    const current = priceRef.current;
    if (!current.priceOnly || !current.contract) return false;
    const ore = stageVariationOre(current.priceAmount);
    if (!Number.isSafeInteger(ore)) {
      setError(t('variationWizard.invalidPrice'));
      return false;
    }
    const request = (async () => {
      setPriceSaving(true);
      setError('');
      try {
        const updated = await contractApi.updateContract(current.contract!.id, {
          contractAmount: String(ore / 100),
          afterRotAmount: String(ore / 100),
        });
        draft.replaceContract(updated);
        setSavedPrice(current.priceAmount);
        await latest.current.onPersisted(updated);
        return true;
      } catch (error) {
        setError(apiErrorText(t, error, 'variationWizard.priceSaveError'));
        return false;
      } finally {
        setPriceSaving(false);
      }
    })();
    priceRequest.current = request;
    try {
      return await request;
    } finally {
      if (priceRequest.current === request) priceRequest.current = null;
    }
  };
  useEffect(() => {
    if (
      !priceOnly ||
      signingLocked ||
      loading ||
      switching ||
      priceSaving ||
      priceAmount === savedPrice ||
      !Number.isSafeInteger(stageVariationOre(priceAmount))
    )
      return;
    const timeout = window.setTimeout(() => {
      void savePrice();
    }, 600);
    return () => window.clearTimeout(timeout);
    // The save reads the latest amount, including edits made during an earlier save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceOnly, priceAmount, savedPrice, priceSaving, loading, switching, signingLocked]);
  const switchMode = async (checked: boolean) => {
    if (signingLocked || modeLocked || switching || !draft.contract) return;
    setSwitching(true);
    setError('');
    review.setReviewed(false);
    try {
      if (!(await draft.save())) return;
      if (priceRequest.current && !(await priceRequest.current)) return;
      const result = await contractApi.changeVariationMode(
        draft.contract.id,
        checked ? 'price_adjustment' : 'milestones',
      );
      draft.replaceContract(result.contract);
      setSettlement(result.replacement);
      setPriceOnly(checked);
      setPriceAmount('0');
      setSavedPrice('0');
      await props.onPersisted(result.contract, result.replacement ?? undefined);
    } catch (error) {
      setError(apiErrorText(t, error, 'variationWizard.modeError'));
    } finally {
      setSwitching(false);
    }
  };
  const saveAll = async () => {
    if (signingLocked) return false;
    if (!(await draft.save())) return false;
    return priceOnly ? savePrice() : plan.valid ? plan.autosave.flush() : false;
  };
  const validatePlan = async () => {
    review.setReviewed(false);
    return priceOnly ? await savePrice() : plan.validate();
  };
  const netDelta = priceOnly
    ? stageVariationOre(priceAmount)
    : plan.totals.netDeltaOre;
  const current = draft.contract;
  const withdrawSigning = async () => {
    if (!current || withdrawing) return;
    setWithdrawing(true);
    setError('');
    try {
      const result = await contractApi.withdrawSigning(current.id);
      draft.replaceContract(result.contract);
      const updatedSettlement = settlement ? { ...settlement, contract: result.contract } : undefined;
      if (updatedSettlement) setSettlement(updatedSettlement);
      draft.setWriteGuard(null);
      setWithdrawn(true);
      review.reset();
      await latest.current.onPersisted(result.contract, updatedSettlement);
    } catch (failure) {
      const code = apiErrorCode(failure);
      setError(t(code === 'SIGNING_ALREADY_COMPLETED'
        ? 'contracts.wizard.edit.withdraw.alreadySigned'
        : code === 'SIGNING_NOT_WITHDRAWABLE'
          ? 'contracts.wizard.edit.withdraw.notWithdrawable'
          : 'contracts.wizard.edit.withdraw.error'));
    } finally {
      setWithdrawing(false);
    }
  };
  const preview = current
    ? {
        ...previewFromContract({
          contract: current,
          contracts: props.contracts,
          milestones: settlement?.milestones ?? [],
          descriptions: {
            ...draft.values,
            otherDescription: [inheritedTerms, draft.values.otherDescription]
              .filter(Boolean)
              .join('\n'),
          },
          company: props.company,
          customer: props.customer,
          attachments: draft.attachments,
          replacement: settlement ?? undefined,
        }),
        theme,
        contractType: 'variation' as const,
        variationNumber: priceOnly
          ? current.variationNumber
          : (current.contractVersion ?? 2) - 1,
        assignmentHtml: `${draft.values.variationReason ? `<p><strong>${t('variationWizard.reason')}: ${t(`variationWizard.reasons.${draft.values.variationReason}`)}</strong></p>` : ''}${draft.values.assignmentDescription}`,
        pricingOverride: settlement ? (
          <VariationChanges
            settlement={settlement}
            projectAdjustmentOre={projectAdjustmentOre}
          />
        ) : (
          <VariationTotals
            parent={totalsParent}
            root={root}
            grossDeltaOre={netDelta}
            netDeltaOre={netDelta}
          />
        ),
        // Refund, unchanged payments or the ordinary terms, from the change being signed.
        paymentTermsKey: netDelta < 0
          ? 'contracts.preview.refundTerms'
          : netDelta === 0
            ? 'contracts.preview.unchangedPaymentTerms'
            : 'contracts.preview.paymentTerms',
        notice: <ConflictVariationNotice contract={current} />,
        onOpenAttachment: props.onOpenAttachment,
        onVerifyAttachment: props.onVerifyAttachment,
      }
    : null;
  const viewOnly = viewing && !signingLocked;
  const dirty =
    draft.dirty ||
    (!priceOnly &&
      (plan.autosave.dirty || plan.autosave.saving || plan.autosave.error)) ||
    (priceOnly && priceAmount !== savedPrice);
  const savedStatus =
    error ||
    (!priceOnly && plan.autosave.error
      ? t('variationWizard.saveError')
      : (!priceOnly && plan.autosave.saving) || priceSaving
        ? t('contracts.wizard.saveStatus.saving')
        : revision
          ? t('contracts.wizard.saveStatus.savedChanges', {
              time: (plan.autosave.savedAt ?? new Date()).toLocaleTimeString(
                'sv-SE',
                { hour: '2-digit', minute: '2-digit' },
              ),
            })
          : draft.saveStatus);
  return (
    <ContractWizardModal
      // Leaving the read-only view starts the wizard over on its review step.
      key={viewOnly ? 'view' : 'edit'}
      mode={props.existing ? 'edit' : 'create'}
      title={viewOnly && props.existing
        ? agreementLabel(props.existing, props.contracts, t)
        : t(signingLocked ? 'variationWizard.pendingTitle' : props.existing ? 'variationWizard.editTitle' : 'variationWizard.title')}
      subtitle={viewOnly ? undefined : props.existing ? agreementLabel(props.existing, props.contracts, t) : t('variationWizard.subtitle')}
      initialStep={viewOnly ? 0 : props.viewOnly ? 2 : props.initialStep}
      dirty={!signingLocked && !viewOnly && dirty}
      saveStatus={savedStatus}
      saving={draft.saving || plan.autosave.saving || priceSaving || switching}
      onSave={signingLocked || viewOnly ? undefined : async () => {
        await saveAll();
      }}
      onClose={() => props.onClose(discarded.current ? undefined : draft.contract ?? undefined)}
      onDiscard={!props.existing && draft.contract ? async () => {
        // Cancelling a new ÄTA removes its unsent draft (ClickUp 869fkhh2r).
        await contractApi.deleteContract(draft.contract!.id)
          .then(() => { discarded.current = true; })
          .catch(() => undefined);
      } : undefined}
      summary={
        <>
          <h3 className="fs-6">
            {draft.values.contractName || t('variationWizard.title')}
          </h3>
          <p className="small">
            {t('contracts.wizard.files.count', {
              count: draft.attachments.length,
            })}
          </p>
          <VariationTotals
            parent={totalsParent}
            root={root}
            grossDeltaOre={priceOnly ? netDelta : plan.totals.grossDeltaOre}
            netDeltaOre={netDelta}
          />
        </>
      }
      banner={
        <>
          {signingLocked && (
            <aside className="alert alert-info" data-testid="variation-signing-gate">
              <strong>{t('variationWizard.pendingAgreement', { agreement: agreementLabel(props.existing!, props.contracts, t) })}</strong>
              <p className="mt-2 mb-0">{t('variationWizard.pendingExplanation')}</p>
              {props.existing?.personSigned ? (
                <p className="mt-2 mb-0">{t('variationWizard.customerAlreadySigned')}</p>
              ) : (
                <>
                  <p className="small mt-2">{t('variationWizard.withdrawExplanation')}</p>
                  <button type="button" className="btn btn-outline-primary variation-wizard-touch"
                    onClick={() => void withdrawSigning()} disabled={loading || withdrawing}>
                    {t(withdrawing ? 'milestoneAttestation.saving' : 'variationWizard.withdrawAndEdit')}
                  </button>
                </>
              )}
            </aside>
          )}
          <div className="border rounded p-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <strong>
                {t(basisVariation === null ? 'variationWizard.basis' : 'variationWizard.basisWithVariations', {
                  number: root.contractNumber, variation: basisVariation,
                })}
              </strong>
              <small className="d-block text-muted">
                {t('variationWizard.effectiveBasis')} ·{' '}
                {variationMoney(
                  Math.round(Number(totalsParent.afterRotAmount) * 100),
                )}{' '}
                {t('variationWizard.afterRot')}
              </small>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary variation-wizard-touch"
              onClick={() => setShowBasis(true)}
            >
              {t('variationWizard.viewBasis')}
            </button>
          </div>
          {entryStage && (
            <aside className="alert alert-warning" data-testid="variation-objection-context">
              <strong>
                <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
                {t('variationWizard.objectionStage', {
                  number: entryStage.milestoneNumber,
                  name: entryStage.description,
                })}
              </strong>
              <p className="small mt-2 mb-0">{t('variationWizard.objectionContext')}</p>
              {entryStage.disputeReason && (
                <p className="small mt-2 mb-0">
                  {t('variationWizard.objectionReason', { reason: entryStage.disputeReason })}
                </p>
              )}
            </aside>
          )}
          {props.reference && (
            <ContractReferenceNote
              contractId={props.existing?.id ?? parent.id}
              message={props.reference}
              locked={false}
            />
          )}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
        </>
      }
      steps={viewOnly ? [{
        key: 'view',
        title: t('contracts.wizard.view.stepTitle'),
        showSummary: false,
        render: () => preview && !loading ? <ContractDocumentPreview {...preview} /> : <p role="status">{t('common.loading')}</p>,
      }] : signingLocked ? [{
        key: 'pending',
        title: t('variationWizard.pendingTitle'),
        showSummary: false,
        render: () => preview && !loading ? <ContractDocumentPreview {...preview} /> : <p role="status">{t('common.loading')}</p>,
      }] : [
        {
          key: 'assignment',
          title: t('variationWizard.assignmentTitle'),
          render: () =>
            loading ? (
              <p role="status">{t('common.loading')}</p>
            ) : (
              <AssignmentStep
                draft={draft}
                stepCounter={t('contracts.wizard.stepper.counter', {
                  current: 1,
                  total: 3,
                })}
                variation={{ inheritedTerms }}
              />
            ),
          validate: () => (loading ? false : draft.validateAndSave()),
        },
        {
          key: 'pricing',
          title: t('variationWizard.planTitle'),
          render: () => (
            <VariationPlanStep
              parent={totalsParent}
              root={root}
              sources={props.stages}
              origins={origins}
              entryStageId={props.entryStageId}
              plan={plan}
              priceOnly={priceOnly}
              modeLocked={modeLocked}
              restorable={timeline?.milestones.filter(
                (stage) => stage.stageReplacement?.kind === 'removed',
              )}
              onModeChange={(checked) => void switchMode(checked)}
              switching={switching || priceSaving || draft.saving}
              priceAmount={priceAmount}
              onPriceAmount={setPriceAmount}
              priceError={error}
            />
          ),
          validate: validatePlan,
        },
        {
          key: 'review',
          title: t('contracts.wizard.steps.review.title'),
          showSummary: false,
          render: () =>
            preview ? (
              <ReviewStep
                review={review}
                preview={preview}
                stepCounter={t('contracts.wizard.stepper.counter', {
                  current: 3,
                  total: 3,
                })}
                providerName={props.providerName}
                variationDeltaOre={netDelta}
                refundOnSigning={!priceOnly}
                messages={messages}
              />
            ) : null,
        },
      ]}
      finalAction={viewOnly
        ? props.canSign && !loading
          ? { label: t('contracts.wizard.view.goToSigning'), onClick: () => setViewing(false) }
          : undefined
        : signingLocked ? undefined : {
        label: t('contracts.wizard.review.signButton', {
          provider: props.providerName,
        }),
        busy: review.signing || switching || plan.checking,
        onClick: async () => {
          await review.sign(async () => {
            if (
              !(await draft.validateAndSave()) ||
              !(await validatePlan()) ||
              !(await saveAll())
            )
              return false;
            sessionStorage.setItem(
              variationReturnKey(parent.projectId),
              draft.contract!.id,
            );
            return true;
          });
        },
      }}
      overlays={
        <>
          {draft.libraryOpen && (
            <AttachmentLibraryPicker
              documents={draft.libraryDocs}
              onAdd={draft.linkFromLibrary}
              onClose={draft.closeLibrary}
            />
          )}
          {showBasis && (
            <Modal
              title={t('variationWizard.viewBasis')}
              onClose={() => setShowBasis(false)}
              size="xl"
              zIndex={1060}
            >
              <ContractDocumentPreview
                {...previewFromContract({
                  contract: parent,
                  contracts: props.contracts,
                  milestones: props.stages,
                  descriptions: basisDescriptions,
                  company: props.company,
                  customer: props.customer,
                  attachments: [],
                })}
                theme={theme}
              />
            </Modal>
          )}
        </>
      }
    />
  );
}
